import React from "react";
import { ArrowUpRight, Instagram } from "lucide-react";
import BusinessGoogleMapsBadge from "./BusinessGoogleMapsBadge";
import "./styles/BusinessExternalLinks.css";

interface BusinessExternalLinksProps {
  businessId: string;
  location: unknown;
  socials: unknown;
}

function instagramUrl(socials: unknown): string | null {
  if (!socials || typeof socials !== "object" || Array.isArray(socials)) return null;
  const value = Object.entries(socials).find(([key]) => key.trim().toLowerCase() === "instagram")?.[1];
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    let input = value.trim();
    // The free-text Connect editor also accepts handles and links without a scheme.
    // Normalize those here instead of silently hiding a saved Instagram entry.
    if (/^@?[A-Za-z0-9._]{1,30}$/.test(input) && !/instagram\.com$/i.test(input)) {
      input = `https://www.instagram.com/${input.replace(/^@/, "")}/`;
    } else if (input.startsWith("//")) {
      input = `https:${input}`;
    } else if (/^(?:(?:www|m)\.)?instagram\.com(?:\/|$)/i.test(input)) {
      input = `https://${input}`;
    }
    const url = new URL(input);
    if (url.protocol === "instagram:" && url.hostname === "user") {
      const username = url.searchParams.get("username");
      return username && /^[A-Za-z0-9._]{1,30}$/.test(username)
        ? `https://www.instagram.com/${username}/`
        : null;
    }
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.port) return null;
    if (!["instagram.com", "www.instagram.com", "m.instagram.com"].includes(url.hostname)) return null;
    url.protocol = "https:";
    return url.href;
  } catch {
    return null;
  }
}

const BusinessExternalLinks: React.FC<BusinessExternalLinksProps> = ({ businessId, location, socials }) => {
  const instagram = instagramUrl(socials);
  return (
    <>
      <BusinessGoogleMapsBadge businessId={businessId} location={location} />
      {instagram && (
        <a className="biz-contact-stat" href={instagram} target="_blank" rel="noopener noreferrer" aria-label="View business on Instagram">
          <div className="biz-contact-stat__top" style={{ color: '#d946ef' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(circle at 20% 105%, #ffd776 0%, #f9a33d 24%, #ed4668 48%, #c13584 68%, #6b4bc3 100%)', color: 'white', borderRadius: '4px', width: '18px', height: '18px' }}>
              <Instagram size={12} strokeWidth={2.5} />
            </div> Find us on
          </div>
          <div className="biz-contact-stat__value">Instagram</div>
          <div className="biz-contact-stat__bottom">
            Open profile <ArrowUpRight size={12} style={{ marginLeft: 2 }} />
          </div>
        </a>
      )}
    </>
  );
};

export default BusinessExternalLinks;
